import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, X } from 'lucide-react';
import type { FilterControl } from '../../lib/exploreColumns';
import type { FilterSpec } from '../../types/list';
import { ColumnFilterControl } from './ColumnFilterControl';

interface Props {
  control: FilterControl;
  spec: FilterSpec;
  onChange: (patch: Partial<FilterSpec>) => void;
  label: string;
}

/** Short human summary of the active filter for the trigger button; null = inactive. */
function activeSummary(control: FilterControl, spec: FilterSpec): string | null {
  switch (control.type) {
    case 'text': {
      const v = spec[control.axis] as string | undefined;
      return v ? `“${v}”` : null;
    }
    case 'enum': {
      const v = (spec[control.axis] as string[] | undefined) ?? [];
      if (!v.length) return null;
      const labelFor = (val: string) => control.options.find((o) => o.value === val)?.label ?? val;
      return v.length <= 2 ? v.map(labelFor).join(', ') : `${v.length} selected`;
    }
    case 'boolean': {
      const v = spec[control.axis] as boolean | undefined;
      return v === undefined ? null : v ? 'Yes' : 'No';
    }
    case 'range': {
      const lo = spec[control.minAxis] as number | undefined;
      const hi = spec[control.maxAxis] as number | undefined;
      if (lo === undefined && hi === undefined) return null;
      return `${lo ?? '…'}–${hi ?? '…'}`;
    }
    case 'dateRange': {
      const f = spec[control.fromAxis] as string | undefined;
      const t = spec[control.toAxis] as string | undefined;
      if (!f && !t) return null;
      return `${f ?? '…'} – ${t ?? '…'}`;
    }
    case 'tags': {
      const v = (spec.tagIds as string[] | undefined) ?? [];
      if (!v.length) return null;
      const mode = spec.tagMode === 'all' ? ' (all)' : '';
      return v.length === 1 ? `1 tag${mode}` : `${v.length} tags${mode}`;
    }
  }
}

function clearControl(control: FilterControl, onChange: (patch: Partial<FilterSpec>) => void): void {
  switch (control.type) {
    case 'text':
    case 'enum':
    case 'boolean':
      onChange({ [control.axis]: undefined } as Partial<FilterSpec>);
      break;
    case 'range':
      onChange({ [control.minAxis]: undefined, [control.maxAxis]: undefined } as Partial<FilterSpec>);
      break;
    case 'dateRange':
      onChange({ [control.fromAxis]: undefined, [control.toAxis]: undefined } as Partial<FilterSpec>);
      break;
    case 'tags':
      onChange({ tagIds: undefined, tagMode: undefined });
      break;
  }
}

/**
 * Compact, dropdown-style per-column filter. The trigger shows the column's active
 * filter summary (blast-lime when set, with a clear ×); clicking opens a small
 * popover holding the actual control. Keeps the filter row to one tidy line per
 * column instead of a wall of expanded pills/inputs.
 */
export function ColumnFilterMenu({ control, spec, onChange, label }: Props) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const summary = activeSummary(control, spec);
  const active = summary !== null;

  // Position the portal'd popover just below the trigger (clamped into the viewport).
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const width = 256; // ~ max-w-[16rem]
    const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
    setPos({ top: r.bottom + 4, left });
  }, [open]);

  // Close on outside click (the popover is portal'd, so check both refs) and on
  // page/table scroll or resize — but NOT when scrolling INSIDE the popover (so the
  // long sport list stays scrollable).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || popRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onScroll = (e: Event) => {
      if (popRef.current && popRef.current.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onResize = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={active ? `${label}: ${summary}` : `Filter ${label}`}
        className={`flex items-center gap-1 max-w-[10rem] rounded-md border px-1.5 py-0.5 text-[11px] font-normal transition-colors ${
          active
            ? 'bg-[#b7ee41] text-[#14310a] border-[#b7ee41]'
            : 'bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
        }`}
      >
        <span className="truncate">{active ? summary : 'Filter'}</span>
        {active ? (
          <X
            className="h-3 w-3 shrink-0 hover:opacity-70"
            role="button"
            aria-label={`Clear ${label} filter`}
            onClick={(e) => {
              e.stopPropagation();
              clearControl(control, onChange);
              setOpen(false);
            }}
          />
        ) : (
          <ChevronDown className="h-3 w-3 shrink-0 opacity-50" />
        )}
      </button>

      {open && pos &&
        createPortal(
          <div
            ref={popRef}
            style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 60 }}
            className="card p-2 min-w-[12rem] max-w-[16rem] shadow-lg"
          >
            <ColumnFilterControl control={control} spec={spec} onChange={onChange} />
          </div>,
          document.body,
        )}
    </div>
  );
}
